import { DirArrow } from "./Arrow";
import { degreesToCompass } from "../lib/scorer";
import { dirLevelColor, fmtSpeed, UNIT_LABELS } from "../lib/utils";
import type {
  CompassDir,
  EnrichedSpot,
  ScoredHour,
  SpeedUnit,
  WindHour,
} from "../lib/types";
import { HourlyChart } from "./HourlyChart";
import styles from "./HourlyForecastCard.module.css";

interface HourlyForecastCardProps {
  spot: EnrichedSpot;
  visibleDays: number;
  unit: SpeedUnit;
  /** The currently-selected hour drives the data summary at the top. */
  selectedHour: WindHour | ScoredHour | null;
  selectedHourIdx: number;
  scrollToDayTick: number;
  scrollToDayOffset: number;
  onSelectHour: (idx: number, dayOffset: number) => void;
  onActiveDayChange: (dayOffset: number) => void;
}

/**
 * Detail-page card combining the wind-summary headline with the hourly
 * bar chart. Figma node 103:19635. The summary at the top reflects the
 * currently-selected hour from the chart below; clicking a bar updates
 * it via `onSelectHour`.
 */
export function HourlyForecastCard({
  spot,
  visibleDays,
  unit,
  selectedHour,
  selectedHourIdx,
  scrollToDayTick,
  scrollToDayOffset,
  onSelectHour,
  onActiveDayChange,
}: HourlyForecastCardProps) {
  return (
    <section className={styles.card}>
      <DataSummary
        hour={selectedHour}
        unit={unit}
        idealDirs={spot.ideal.dirs}
      />
      <HourlyChart
        spot={spot}
        visibleDays={visibleDays}
        unit={unit}
        selectedHourIdx={selectedHourIdx}
        scrollToDayTick={scrollToDayTick}
        scrollToDayOffset={scrollToDayOffset}
        onSelectHour={onSelectHour}
        onActiveDayChange={onActiveDayChange}
      />
    </section>
  );
}

interface DataSummaryProps {
  hour: WindHour | ScoredHour | null;
  unit: SpeedUnit;
  idealDirs: readonly CompassDir[];
}

/**
 * Top of the hourly forecast card — large speed + direction display.
 * Figma node 103:19610 (Hourly forecast card / data summary).
 *
 * Arrow accent uses `dirLevelColor()` so its colour reflects direction
 * match only (consistent with all other arrows in the app — see
 * `docs/decisions.md` for the decoupling rationale).
 */
function DataSummary({ hour, unit, idealDirs }: DataSummaryProps) {
  if (!hour) {
    return (
      <div className={styles.summary}>
        <div className={styles.windSpeed}>
          <div className={styles.windSpeedRow}>
            <span className={styles.speed}>—</span>
            <span className={styles.unit}>{UNIT_LABELS[unit]}</span>
          </div>
        </div>
      </div>
    );
  }

  const dirLabel = degreesToCompass(hour.dirDeg);
  const speedKn = hour.speedMs * 1.944;
  const accent =
    speedKn < 3
      ? "var(--foreground-muted)"
      : dirLevelColor(hour.dirDeg, idealDirs);

  return (
    <div className={styles.summary}>
      <div className={styles.windSpeed}>
        <div className={styles.windSpeedRow}>
          <span className={styles.speed}>{fmtSpeed(hour.speedMs, unit)}</span>
          <span className={styles.unit}>{UNIT_LABELS[unit]}</span>
        </div>
        <span className={styles.gust}>↑ {fmtSpeed(hour.gustMs, unit)} gusts</span>
      </div>
      <div className={styles.windDir}>
        <div className={styles.dirRow}>
          <DirArrow dirDeg={hour.dirDeg} color={accent} size={40} />
          <span className={styles.dir}>{dirLabel}</span>
        </div>
        <span className={styles.dirDeg}>{Math.round(hour.dirDeg)}°</span>
      </div>
    </div>
  );
}
